import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconContext } from "react-icons";
import {
  FaLink,
  FaFacebookF,
  FaWhatsapp,
  FaTwitter,
  FaLinkedinIn,
  FaPinterestP,
  FaCalendarDay,
  FaFacebookMessenger,
} from "react-icons/fa";
import { SocialLink } from "./SocialLink.js";
import "./Blogs.css";
import Seo from "../seo/Seo";
import Breadcrumbs from "./Breadcrumbs";
import { SITE_ORIGIN } from "../seo/site";
import { cleanBlogSlug } from "../seo/blogSlug";
import { blogPath, prepareBlogHtml, resolveBlogPath, usableMeta } from "../seo/blogPaths";

const API = process.env.REACT_APP_BASE_URL;

/** Post details for an API slug, or null when the API says the post doesn't exist. */
async function getBlogDetails(rawSlug) {
  try {
    const response = await axios.get(
      `${API}/v1/api/public-api/get-blog-details/${encodeURIComponent(rawSlug)}`
    );
    return response.data?.blogs ? response.data : null;
  } catch (error) {
    if (error.response?.status === 404) return null;
    throw error;
  }
}

/** Posts published after the last build aren't in the slug map, so find the API slug by the clean URL. */
async function findPostByCleanSlug(cleanSlug) {
  const { data } = await axios.get(`${API}/v1/api/public-api/category-with-blogs`);
  const posts = [
    ...(data?.getLatestBlogs || []),
    ...(data?.categoryWithBlogs || []).flatMap((category) => category.Blogs || []),
  ];
  return posts.find((post) => post?.slug && cleanBlogSlug(post.slug, post.title) === cleanSlug);
}

async function loadBlog(pathSlug) {
  const resolved = resolveBlogPath(pathSlug);
  if (resolved.redirectTo) return { redirectTo: resolved.redirectTo };
  const data = await getBlogDetails(resolved.raw);
  if (data || resolved.mapped) return { data };
  const post = await findPostByCleanSlug(resolved.raw);
  return { data: post ? await getBlogDetails(post.slug) : null };
}

/** Post saved into the prerendered page (or by an earlier visit), so it shows without a loading state. */
function savedBlog(id) {
  const page = typeof window !== "undefined" ? window.__CONNCT_PAGE__ : null;
  return page && page.type === "blog" && page.key === id ? page.data : null;
}

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blogData, setBlogData] = useState(() => savedBlog(id));
  const [status, setStatus] = useState(() => (savedBlog(id) ? "ready" : "loading"));
  const [attempt, setAttempt] = useState(0);
  const AWS_S3_BUCKET_URL = process.env.REACT_APP_AWS_S3_BUCKET_URL;

  useEffect(() => {
    let cancelled = false;
    const saved = savedBlog(id);
    setBlogData(saved);
    setStatus(saved ? "ready" : "loading");
    loadBlog(id)
      .then((result) => {
        if (cancelled) return;
        if (result.redirectTo) {
          navigate(result.redirectTo, { replace: true });
        } else if (result.data) {
          // scripts/prerender.js copies this into the saved HTML.
          window.__CONNCT_PAGE__ = { type: "blog", key: id, data: result.data };
          setBlogData(result.data);
          setStatus("ready");
        } else {
          setStatus("missing");
        }
      })
      .catch(() => {
        // A failed refresh keeps the saved article; only show an error when there is nothing to show.
        if (!cancelled && !saved) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate, attempt]);

  function capitalizeFirstLetter(string) {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  const getDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  if (status === "missing") {
    return (
      <div className="container" style={{ marginTop: "100px" }} data-page-state="missing">
        <Seo
          title="Article not found | Connct"
          description="This Connct blog article could not be found."
          path="/blogs"
          noindex
        />
        <h1>Article not found</h1>
        <p>This article is not available.</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="container" style={{ marginTop: "100px", minHeight: "70vh" }} data-page-state="error">
        <Breadcrumbs
          items={[
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blogs" },
          ]}
        />
        <p>We couldn't load this article. Please check your connection and try again.</p>
        <button type="button" className="btn btn-primary" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </button>
      </div>
    );
  }

  if (status !== "ready" || !blogData?.blogs) {
    return (
      <div className="container" style={{ marginTop: "100px", minHeight: "70vh" }} data-page-state="loading">
        <Breadcrumbs
          items={[
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blogs" },
          ]}
        />
        <p>Loading...</p>
      </div>
    );
  }

  const blog = blogData.blogs;
  const plainBody = (blog.description || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const title = usableMeta(blog.metaTitle) || blog.title;
  const description =
    usableMeta(blog.metaDesc) ||
    plainBody.slice(0, 160) ||
    "Read this article from the Connct blog on digital business cards and networking.";
  const canonicalPath = blogPath(blog.slug, blog.title) || `/blogs/${id}`;
  const shareUrl = `${SITE_ORIGIN}${canonicalPath}`;
  const encodedShareUrl = encodeURIComponent(shareUrl);

  return (
    <div className="container" style={{ marginTop: "100px" }} data-page-state="ready">
      <Seo
        title={`${title} | Connct`}
        description={description}
        path={canonicalPath}
        type="article"
        image={
          blog.image
            ? `https://${AWS_S3_BUCKET_URL}.s3.ap-south-1.amazonaws.com/${blog.image}`
            : undefined
        }
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blogs" },
          { name: blog.title, path: canonicalPath },
        ]}
      />
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blogs" },
          { name: blog.title, path: canonicalPath },
        ]}
      />
      <h1>{blog.title}</h1>
      <div className="category-tag">
        <span style={{ color: "red" }}>
          {capitalizeFirstLetter(blog.Category?.name)}
        </span>{" "}
        <span style={{ color: "green" }}>
          {capitalizeFirstLetter(blog.Subcategory?.name)}
        </span>
      </div>

      <div className="date">
        <FaCalendarDay style={{ marginLeft: 0 }} />
        <div style={{ lineHeight: "2em" }}>
          &nbsp;{getDate(blog.createdAt)}
        </div>
      </div>
      {blog.image ? (
        <img
          alt={blog.title || "Connct blog article"}
          className="img-fluid detail-image"
          src={`https://${AWS_S3_BUCKET_URL}.s3.ap-south-1.amazonaws.com/${blog.image}`}
          width={1200}
          height={675}
        />
      ) : null}
      <div className="icons">
        <IconContext.Provider value={{ className: "react-icons" }}>
          <button
            className="icon-btn"
            style={{ backgroundColor: "#607D8B" }}
            onClick={() => navigator.clipboard.writeText(shareUrl)}
          >
            <FaLink />
          </button>
          <SocialLink
            color="#4267B2"
            targetLink={`https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`}
          >
            <FaFacebookF />
          </SocialLink>
          <SocialLink
            color="#25D366"
            targetLink={`https://web.whatsapp.com/send?text=${encodedShareUrl}`}
            dataAction="share/whatsapp/share"
          >
            <FaWhatsapp />
          </SocialLink>
          <SocialLink
            color="#00ACEE"
            targetLink={`https://twitter.com/intent/tweet?text=${encodedShareUrl}`}
          >
            <FaTwitter />
          </SocialLink>
          <SocialLink
            color="#006FA6"
            targetLink={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`}
          >
            <FaLinkedinIn />
          </SocialLink>
          <SocialLink
            color="#BD091D"
            targetLink={`https://www.pinterest.com/pin/create/button/?url=${encodedShareUrl}`}
          >
            <FaPinterestP />
          </SocialLink>
          <SocialLink color="#0080FF">
            <FaFacebookMessenger />
          </SocialLink>
        </IconContext.Provider>
      </div>
      <div
        className="blog-description"
        dangerouslySetInnerHTML={{
          __html: prepareBlogHtml(blog.description, blog.title),
        }}
      ></div>
    </div>
  );
};

export default BlogDetails;
